import { Injectable } from '@angular/core';
import * as _ from 'lodash-es';
import { UserRest } from './core.user.rest.service';
import { UiService } from './core.ui.service';
import { StateService } from '@uirouter/angular';
import { SiteConfigService } from './core.site-config.service';

const featureDefinitions = require('../../../features.json');

@Injectable()
export class FeatureFlagService {

  // Pre-calculated public state — read directly from templates and components.
  public featureFlagsEnabled: boolean = false;
  public featureLevel: number = 1;
  public readonly featureLevels = {
    Released:     1,
    Stable:       2,
    Test:         4,
    Experimental: 8
  };

  // Lookup map built from features.json client-features, keyed by feature name.
  private featureFlags: { [key: string]: boolean } = {};

  // Feature definitions loaded from features.json at module load time.
  private readonly clientFeatures: { [key: string]: number } =
    _.get(featureDefinitions, 'client-features', {});

  constructor(
    private userRest: UserRest,
    private uiService: UiService,
    private stateService: StateService,
    private siteConfigService: SiteConfigService,
  ) { }

  /**
   * Called from the route resolve chain after session recovery.
   * Determines whether feature flags are enabled for this environment,
   * loads the user's saved feature level, and rebuilds the flags map.
   */
  public init(unixName: string): Promise<void> {
    const siteConfig = this.siteConfigService.getSiteConfig();
    const appEnv = _.get(siteConfig, 'appEnvironment', 'production');
    const enableFeatureFlagSwitch = _.get(siteConfig, 'enableFeatureFlagSwitch', true);
    this.featureFlagsEnabled =
      !['production'].includes(appEnv) &&
      enableFeatureFlagSwitch;

    return this.loadUserFeatureLevel(unixName)
      .then(level => this.applyFeatureLevel(level));
  }

  /**
   * Returns true if the named client feature is active for the current
   * user's feature level. Always false when feature flags are not enabled.
   */
  public isFeatureEnabled(featureKey: string): boolean {
    if (!this.featureFlagsEnabled) return false;
    return this.featureFlags[featureKey] ?? false;
  }

  /**
   * Persists the user's chosen feature level, then reloads the current state
   * so the directive re-evaluates for all feature-flagged elements.
   */
  public setFeatureLevel(unixName: string, name: string, level: number): void {
    if (this.featureLevel === level) return;
    this.userRest.saveUserPreference(unixName, 'feature.level', String(level))
      .then(() => {
        this.uiService.message.showSuccess('Feature level is now ' + name, 'Set Feature Level');
        this.stateService.reload();
      })
      .catch(() => {
        this.uiService.message.showError('Sorry, could not set feature level to ' + name, 'Set Feature Level');
      });
  }

  // Fetches the user's saved feature level preference from the API.
  // Returns the Released level (1) for anonymous users or on error.
  private loadUserFeatureLevel(unixName: string): Promise<number> {
    if (!unixName || unixName === 'None') {
      return Promise.resolve(this.featureLevels.Released);
    }
    return this.userRest.getUserPreference(unixName, 'feature.level', { default: this.featureLevels.Released })
      .then((result: any) => _.get(result, 'preferenceValue', this.featureLevels.Released))
      .catch(() => this.featureLevels.Released);
  }

  // Stores the effective feature level and rebuilds the flags lookup map.
  // When feature flags are disabled, the level is pinned to Released.
  private applyFeatureLevel(level: number): void {
    this.featureLevel = this.featureFlagsEnabled ? level : this.featureLevels.Released;
    this.featureFlags = {};
    _.forEach(this.clientFeatures, (minLevel, key) => {
      this.featureFlags[key] = minLevel <= this.featureLevel;
    });
  }
}
